import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import OnboardingStepper from '../components/OnboardingStepper';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, Anchor, Shield } from 'lucide-react';

const ForcePasswordChangePage = () => {
  const { api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);
    try {
      await api.put('/auth/set-initial-password', { new_password: newPassword });
      toast.success('Bienvenue dans l\'Équipage ! Personnalise ton profil pour démarrer.');
      if (refreshUser) await refreshUser();
      // Circle-style : after first password change → land directly on profile setup
      navigate('/profile?onboarding=true');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="force-password-change-page">
      <div className="noise-overlay" />
      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <div className="relative w-20 h-20 mx-auto mb-4">
            <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
              <circle cx="50" cy="50" r="46" fill="none" stroke="hsl(var(--gold))" strokeWidth="1" strokeDasharray="2 4" opacity="0.6" />
              <circle cx="50" cy="50" r="38" fill="none" stroke="hsl(var(--gold))" strokeWidth="0.8" opacity="0.35" />
            </svg>
            <div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white flex items-center justify-center"
              style={{
                border: '1.5px solid hsl(var(--gold))',
                boxShadow: '0 4px 16px rgba(40,28,18,0.18), inset 0 0 0 3px hsl(var(--paper))',
                padding: '7px',
              }}
            >
              <img src="/logo-192.png" alt="Odys" className="w-full h-full object-contain" />
            </div>
          </div>
          <h1 className="font-heading font-bold text-2xl">L'Équipage <span className="text-primary">Odys</span></h1>
        </div>
        <OnboardingStepper step={1} />

        <Card className="card-base border-orange-500/30">
          <CardHeader className="text-center">
            <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center mx-auto mb-2">
              <Shield className="w-6 h-6 text-orange-500" />
            </div>
            <CardTitle className="text-xl">Creer ton mot de passe</CardTitle>
            <CardDescription>
              Pour securiser ton compte, choisis un nouveau mot de passe personnel.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="new-password">Nouveau mot de passe</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Minimum 6 caracteres"
                    className="pr-10"
                    data-testid="new-password-input"
                    autoFocus
                  />
                  <Button type="button" variant="ghost" size="icon" className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
                {newPassword && newPassword.length < 6 && (
                  <p className="text-xs text-red-400">Minimum 6 caracteres requis</p>
                )}
                {newPassword && newPassword.length >= 6 && (
                  <p className="text-xs text-green-400">Mot de passe valide</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirmer le mot de passe</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Retape ton mot de passe"
                  data-testid="confirm-password-input"
                />
                {confirmPassword && confirmPassword !== newPassword && (
                  <p className="text-xs text-red-400">Les mots de passe ne correspondent pas</p>
                )}
                {confirmPassword && confirmPassword === newPassword && newPassword.length >= 6 && (
                  <p className="text-xs text-green-400">Les mots de passe correspondent</p>
                )}
              </div>

              <Button type="submit" className="w-full bg-gradient-to-r from-primary to-primary/80" disabled={loading || newPassword.length < 6 || newPassword !== confirmPassword} data-testid="set-password-btn">
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Enregistrement...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    Definir mon mot de passe
                  </div>
                )}
              </Button>
            </form>

            <div className="mt-4 p-3 bg-secondary/30 rounded-lg">
              <p className="text-xs text-muted-foreground text-center">
                Ce mot de passe remplacera le mot de passe provisoire recu par email.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForcePasswordChangePage;
